import { Component as ReactComponent, type ReactNode } from "react";
import { EmptyState } from "../../molecules/EmptyState/index.js";
import { shallowEqualArray } from "./helpers.js";
import type { DashboardItem } from "./types.js";

/**
 * Per-widget error boundary. A throwing body falls back to an inline message;
 * the rest of the dashboard keeps rendering. **Recoverable**: when `resetKeys`
 * change (a new `instanceId`, or a new `params` ref after a successful edit) the
 * boundary clears its error and re-renders. Host contract: `params` must be a
 * new ref on edit AND stable otherwise (memoized), or a genuinely-broken widget
 * would re-mount every render.
 */
export class WidgetErrorBoundary extends ReactComponent<
  {
    widgetName: string;
    resetKeys: readonly unknown[];
    children: ReactNode;
    /** Optional custom fallback slot (e.g. a board-level "Reload" affordance).
     *  When omitted, an inline "{widgetName} failed to render." message is
     *  shown. */
    fallbackSlot?: ReactNode;
  },
  { error: boolean; prevKeys: readonly unknown[] }
> {
  constructor(props: {
    widgetName: string;
    resetKeys: readonly unknown[];
    children: ReactNode;
    fallbackSlot?: ReactNode;
  }) {
    super(props);
    this.state = { error: false, prevKeys: props.resetKeys };
  }

  static getDerivedStateFromError(): { error: boolean } {
    return { error: true };
  }

  static getDerivedStateFromProps(
    props: { resetKeys: readonly unknown[] },
    state: { error: boolean; prevKeys: readonly unknown[] },
  ): { error: boolean; prevKeys: readonly unknown[] } | null {
    if (shallowEqualArray(props.resetKeys, state.prevKeys)) return null;
    // resetKeys changed → re-arm (clear any error) and remember the new keys.
    return { error: false, prevKeys: props.resetKeys };
  }

  override componentDidCatch(): void {
    /* swallowed — surfaced inline via render() */
  }

  override render(): ReactNode {
    if (this.state.error) {
      if (this.props.fallbackSlot !== undefined) return this.props.fallbackSlot;
      /* A region that failed is EmptyState error, the one-line row
         (OK-31), centred in the cell. */
      return (
        <div className="h-full w-full flex items-center justify-center px-3 py-2">
          <EmptyState
            variant="error"
            size="sm"
            title={`${this.props.widgetName} failed to render.`}
          />
        </div>
      );
    }
    return this.props.children;
  }
}

/**
 * Invokes `renderWidget` LAZILY inside the boundary's own subtree. Without
 * this, `renderWidget(item)` would run in the parent's render (computing the
 * `children` prop) and a throw would escape the boundary and crash the whole
 * dashboard. As a child component, its throw is caught by `WidgetErrorBoundary`.
 */
export function WidgetSlot({
  item,
  renderWidget,
}: {
  item: DashboardItem | undefined;
  renderWidget: (item: DashboardItem) => ReactNode;
}) {
  // Defensive: React's dev-mode error-boundary recovery can re-invoke this with
  // a momentarily-undefined `item`; never hand `renderWidget` an undefined.
  if (!item) return null;
  return <>{renderWidget(item)}</>;
}
